import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAdminStore } from '../../stores/adminStore';
import { useTranslation } from '../../hooks/useTranslation';
import { format } from 'date-fns';
import { format as timeAgo } from 'timeago.js';
import { toLocalizedFormat, UNIVERSAL_DATE_FORMAT } from '@dutchinvoice/shared-utils';
import { useInvoiceStore } from '../../stores/invoiceStore';
import { LocalizedLink } from '../../components/LocalizedLink';

export const UserSchedules: React.FC = () => {
  const { t } = useTranslation();
  const language = useInvoiceStore(state => state.settings.language);
  const { uid } = useParams<{ uid: string }>();
  const {
    selectedUser,
    userSchedules,
    userSchedulesLoading,
    userSchedulesError,
    fetchUserDetails,
    fetchUserSchedules
  } = useAdminStore();

  useEffect(() => {
    if (uid) {
      fetchUserDetails(uid);
      fetchUserSchedules(uid);
    }
  }, [uid, fetchUserDetails, fetchUserSchedules]);

  const getScheduleTypeLabel = (type: string) => {
    switch (type) {
      case 'monthly':
        return t('schedule.monthly');
      case 'quarterly':
        return t('schedule.quarterly');
      case 'yearly':
        return t('schedule.yearly');
      default:
        return type;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/50 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <LocalizedLink
              to="/admin"
              className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-2"
            >
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('admin.backToAdmin')}
            </LocalizedLink>
            <h1 className="text-2xl font-semibold text-gray-900">
              {selectedUser ? t('admin.userSchedules.title', { email: selectedUser.email }) : t('admin.userSchedules.title', { email: '...' })}
            </h1>
          </div>
        </div>

        {/* Schedules Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-black/5 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100">
            <h2 className="text-lg font-medium text-gray-900">{t('admin.userSchedules.schedules')}</h2>
          </div>
          
          {userSchedulesLoading ? (
            <div className="p-6 text-center text-gray-500">{t('common.loading')}</div>
          ) : userSchedulesError ? (
            <div className="p-6 text-center text-red-500">{userSchedulesError}</div>
          ) : userSchedules.length === 0 ? (
            <div className="p-6 text-center text-gray-500">{t('admin.userSchedules.noSchedules')}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead>
                  <tr className="bg-gray-50/50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('admin.userSchedules.templateInvoice')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('admin.userSchedules.type')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('admin.userSchedules.startDate')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('admin.userSchedules.endDate')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('admin.userSchedules.lastGenerated')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('admin.userSchedules.nextDate')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('admin.userSchedules.status')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {userSchedules.map((schedule) => {
                    const startDate = format(new Date(schedule.startDate), UNIVERSAL_DATE_FORMAT);
                    const endDate = schedule.endDate ? format(new Date(schedule.endDate), UNIVERSAL_DATE_FORMAT) : null;
                    const lastGenerated = schedule.lastGeneratedDate ? format(new Date(schedule.lastGeneratedDate), UNIVERSAL_DATE_FORMAT) : null;
                    const nextDate = schedule.nextInvoiceDate ? format(new Date(schedule.nextInvoiceDate), UNIVERSAL_DATE_FORMAT) : null;
                    
                    return (
                      <tr key={schedule.id} className="hover:bg-gray-50/50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <LocalizedLink 
                            to={`/admin/invoice/${schedule.templateInvoiceId}`}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            {schedule.templateInvoiceId}
                          </LocalizedLink>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {getScheduleTypeLabel(schedule.type)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {toLocalizedFormat(startDate, language)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {endDate ? toLocalizedFormat(endDate, language) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {lastGenerated ? (
                            <>
                              {toLocalizedFormat(lastGenerated, language)}
                              <div className="text-xs text-gray-400 mt-0.5">
                                {timeAgo(new Date(schedule.lastGeneratedDate!))}
                              </div>
                            </>
                          ) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {nextDate ? toLocalizedFormat(nextDate, language) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            schedule.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {schedule.active ? t('admin.userSchedules.active') : t('admin.userSchedules.inactive')}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 